import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Button, Select, Form, Modal, Table, Tag, Space, message,
  Tabs, Steps, Alert, Descriptions, Tooltip, Popconfirm,
  Typography, Row, Col, Empty, Input, Checkbox, InputNumber,
} from 'antd';
import {
  SyncOutlined, EyeOutlined, CheckCircleOutlined, RollbackOutlined,
  HistoryOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined,
  ExclamationCircleOutlined, KeyOutlined, StarFilled, EditOutlined,
} from '@ant-design/icons';
import { syncService, syncBaseService, syncTokenService } from '../services/syncService';
import { companyService } from '../services/companyService';
import { tenantService } from '../services/tenantService';
import { authService } from '../services/authService';
import { useI18n } from '../hooks/useI18n';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Step } = Steps;

interface DiffEntry {
  entityType: string;
  key: string;
  operation: 'added' | 'modified' | 'deleted' | 'unchanged';
  sourceFingerprint?: string;
  targetFingerprint?: string;
  fields?: Record<string, { before: unknown; after: unknown }>;
  sourceData?: Record<string, unknown>;
}

interface SyncJob {
  id: string;
  source_tenant?: string;
  source_project?: string;
  target_tenant?: string;
  target_project?: string;
  entity_types?: string;
  mode?: string;
  status?: string;
  started_at?: string;
  finished_at?: string;
  applied_summary?: string;
  triggered_by?: string;
}

interface BaseRecord {
  id: number;
  scope: 'tenant' | 'global';
  tenantCode?: string;
  projectId: string;
  entityTypes: string[];
  description?: string;
  createdBy?: string;
  createdAt?: string;
}

interface TokenRecord {
  alias: string;
  baseUrl: string;
  scope: { entityTypes: string[] };
  direction?: 'outbound' | 'inbound';
  createdBy?: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  createdAt?: string;
}

interface ProjectOption {
  value: string;   // project_id
  label: string;   // company name(s) 或 fallback "Project N"
}

const ENTITY_TYPES = ['instruction', 'modeling', 'sql_pair'] as const;
const DEFAULT_ENTITY_TYPES = [...ENTITY_TYPES];

const opTagColor: Record<string, string> = {
  added: 'green', modified: 'orange', deleted: 'red', unchanged: 'default',
};

const SyncManager: React.FC = () => {
  const { t } = useI18n();
  const MODES = [
    { value: 'incremental', label: t('syncManager.wizard.modeIncremental') },
    { value: 'full-override', label: t('syncManager.wizard.modeFullOverride') },
  ];
  // ── Wizard state ──────────────────────────────────────────────────────────
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardForm] = Form.useForm();
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<{
    previewId: string;
    expiresAt: string;
    summary: Record<string, { added: number; modified: number; deleted: number; unchanged: number }>;
    diffs: DiffEntry[];
    totalDiffs: number;
  } | null>(null);
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [defaultAction, setDefaultAction] = useState<string>('overwrite');
  const [diffDetailRow, setDiffDetailRow] = useState<DiffEntry | null>(null);

  // ── Base Projects state ──────────────────────────────────────────────────
  const [baseList, setBaseList] = useState<BaseRecord[]>([]);
  const [globalBaseList, setGlobalBaseList] = useState<BaseRecord[]>([]);
  const [baseLoading, setBaseLoading] = useState(false);
  const [baseModalOpen, setBaseModalOpen] = useState(false);
  const [baseForm] = Form.useForm();

  // ── History state ────────────────────────────────────────────────────────
  const [history, setHistory] = useState<SyncJob[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // null = current tenant; '' (空字符串) = "All tenants" 聚合（仅超管可用）
  const [historyViewTenant, setHistoryViewTenant] = useState<string | null>(null);

  // ── Base view tenant (selector on Base Projects tab) ─────────────────────
  const [baseViewTenant, setBaseViewTenant] = useState<string | null>(null);

  // ── Tokens state ─────────────────────────────────────────────────────────
  const [tokens, setTokens] = useState<TokenRecord[]>([]);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenScopeModalOpen, setTokenScopeModalOpen] = useState(false);
  const [editingToken, setEditingToken] = useState<TokenRecord | null>(null);
  // 当前正在 register 的方向（'outbound' 或 'inbound'），点不同的 Register 按钮时设置
  const [tokenModalDirection, setTokenModalDirection] = useState<'outbound' | 'inbound'>('outbound');
  const [tokenForm] = Form.useForm();
  const [tokenScopeForm] = Form.useForm();

  // ── Project options (per-side, loaded by tenant code) ───────────────────
  const [sourceProjectOptions, setSourceProjectOptions] = useState<ProjectOption[]>([]);
  const [targetProjectOptions, setTargetProjectOptions] = useState<ProjectOption[]>([]);
  const [baseProjectOptions, setBaseProjectOptions] = useState<ProjectOption[]>([]);
  const [sourceProjectsLoading, setSourceProjectsLoading] = useState(false);
  const [targetProjectsLoading, setTargetProjectsLoading] = useState(false);

  // ── Tenant options (Source/Target Tenant Code 下拉) ─────────────────────
  const [tenantOptions, setTenantOptions] = useState<{ value: string; label: string }[]>([]);
  // 用 ref 持有最新 tenantOptions，让 loaders（loadBase/loadHistory）在不把
  // tenantOptions 列入依赖的前提下也能拿到最新值，避免 setTenantOptions 触发的
  // 引用变化引起 loaders → useEffect → loadTenants 死循环。
  const tenantOptionsRef = useRef(tenantOptions);
  useEffect(() => { tenantOptionsRef.current = tenantOptions; }, [tenantOptions]);

  // 与 authService.isSuperAdmin() 保持一致（user.isSuperAdmin 是 camelCase）
  const isSuperAdmin = authService.isSuperAdmin();

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadBase = useCallback(async () => {
    setBaseLoading(true);
    try {
      // baseViewTenant: null=当前租户 / '' = ALL（仅超管） / 具体 tenantCode
      const targetTenant = baseViewTenant;
      if (targetTenant === '' && isSuperAdmin) {
        // 聚合：依次拉取 tenantOptionsRef.current 中所有租户
        const all: BaseRecord[] = [];
        for (const opt of tenantOptionsRef.current) {
          try {
            const r = await syncBaseService.list(opt.value);
            const items: BaseRecord[] = r?.data?.items || r?.items || r?.data || [];
            all.push(...items);
          } catch (e) {
            // 单个租户失败不阻塞其他
            console.warn(`loadBase(${opt.value}) failed`, e);
          }
        }
        setBaseList(all);
      } else {
        const r = await syncBaseService.list(targetTenant ?? undefined);
        setBaseList(r?.data?.items || r?.items || r?.data || []);
      }
      if (isSuperAdmin) {
        try {
          const g = await syncBaseService.listGlobal();
          setGlobalBaseList(g?.data?.items || g?.items || g?.data || []);
        } catch (e) { /* non-fatal */ }
      }
    } catch (e: any) {
      message.error(t('syncManager.msgs.loadBaseFailed', { error: e.message }));
    } finally {
      setBaseLoading(false);
    }
    // 注意：tenantOptions 通过 ref 读取，不放进依赖里，避免 loaders 引用变化引发的循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, baseViewTenant]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const targetTenant = historyViewTenant;
      if (targetTenant === '' && isSuperAdmin) {
        // 聚合所有租户
        const all: SyncJob[] = [];
        for (const opt of tenantOptionsRef.current) {
          try {
            const r = await syncService.history({ page: 1, size: 50 }, opt.value);
            const items: SyncJob[] = r?.data?.items || r?.items || r?.data || [];
            all.push(...items);
          } catch (e) {
            console.warn(`loadHistory(${opt.value}) failed`, e);
          }
        }
        // 按 started_at 降序
        all.sort((a, b) => {
          const ta = a.started_at ? new Date(a.started_at).getTime() : 0;
          const tb = b.started_at ? new Date(b.started_at).getTime() : 0;
          return tb - ta;
        });
        setHistory(all);
      } else {
        const r = await syncService.history({ page: 1, size: 50 }, targetTenant ?? undefined);
        setHistory(r?.data?.items || r?.items || r?.data || []);
      }
    } catch (e: any) {
      message.error(t('syncManager.msgs.loadHistoryFailed', { error: e.message }));
    } finally {
      setHistoryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, historyViewTenant]);

  const loadTokens = useCallback(async () => {
    if (!isSuperAdmin) return;
    setTokenLoading(true);
    try {
      const r = await syncTokenService.list();
      setTokens(r?.data?.items || r?.items || r?.data || []);
    } catch (e: any) {
      message.error(t('syncManager.msgs.loadTokensFailed', { error: e.message }));
    } finally {
      setTokenLoading(false);
    }
  }, [isSuperAdmin]);

  const loadProjectsForTenant = useCallback(async (tenantCode: string): Promise<ProjectOption[]> => {
    const code = (tenantCode || '').trim();
    if (!code) return [];
    try {
      const r = await companyService.getCompanies(code);
      const companies = r?.data || [];
      const byPid = new Map<string, string[]>();
      for (const c of companies as any[]) {
        if (c?.project_id == null || String(c.project_id).trim() === '') continue;
        const pid = String(c.project_id);
        if (!byPid.has(pid)) byPid.set(pid, []);
        if (c.company_name) byPid.get(pid)!.push(c.company_name);
      }
      return Array.from(byPid.entries())
        .sort((a, b) => Number(a[0]) - Number(b[0]) || a[0].localeCompare(b[0]))
        .map(([pid, names]) => ({
          value: pid,
          label: names.length ? `${pid} - ${names.join(', ')}` : `Project ${pid}`,
        }));
    } catch (e) {
      // 跨租户访问可能因权限失败，不阻塞 UI
      console.warn(`loadProjectsForTenant(${code}) failed`, e);
      return [];
    }
  }, []);

  // 加载 Source/Target Tenant Code 下拉所需的租户列表：
  // - 超管 → /tenants 全量
  // - 普通用户 → 用登录时下发的 storedUser.tenants[] 构造（避免调用未实现的 /tenants/user）
  const loadTenants = useCallback(async () => {
    try {
      if (isSuperAdmin) {
        const tenants = await tenantService.getAllTenants();
        setTenantOptions(
          (tenants || [])
            .filter((tn) => !!tn?.code)
            .map((tn) => ({
              value: tn.code,
              label: tn.name ? `${tn.code} - ${tn.name}` : tn.code,
            }))
        );
        return;
      }
      const storedUser = authService.getStoredUser();
      const userTenants = storedUser?.tenants || [];
      if (userTenants.length > 0) {
        setTenantOptions(
          userTenants
            .filter((tn) => !!tn?.code)
            .map((tn) => ({
              value: tn.code,
              label: tn.name ? `${tn.code} - ${tn.name}` : tn.code,
            }))
        );
        return;
      }
      // fallback: 至少把当前租户放进去，让下拉可用
      const cur = localStorage.getItem('currentTenant') || storedUser?.tenantCode;
      if (cur) {
        setTenantOptions([{ value: cur, label: cur }]);
      }
    } catch (e) {
      console.warn('loadTenants failed', e);
    }
  }, [isSuperAdmin]);

  // Base 项目仍按"当前登录租户"加载（Set Base 只能为自己租户的项目设 Base）
  const loadBaseProjectOptions = useCallback(async () => {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    setBaseProjectOptions(await loadProjectsForTenant(tenantCode));
  }, [loadProjectsForTenant]);

  // mount 时只拉一次的静态数据：tokens / tenants / base 项目下拉
  useEffect(() => {
    loadTokens();
    loadTenants();
    loadBaseProjectOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // baseViewTenant / historyViewTenant 变化时重新加载对应数据
  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── Wizard handlers ──────────────────────────────────────────────────────
  const handlePreview = async () => {
    try {
      const v = await wizardForm.validateFields();
      const sourceSystem = v.sourceSystem || 'local';
      if (!v.useBase && sourceSystem !== 'local') {
        const outbound = tokens.find(
          (tk) => tk.alias === sourceSystem && (tk.direction ?? 'outbound') === 'outbound'
        );
        const allowed = outbound?.scope?.entityTypes ?? [];
        if (allowed.length > 0) {
          const missing = (v.entityTypes as string[]).filter((et) => !allowed.includes(et));
          if (missing.length > 0) {
            message.error(
              t('syncManager.msgs.tokenScopeMismatch', {
                alias: sourceSystem,
                missing: missing.join(', '),
                allowed: allowed.join(', '),
              })
            );
            return;
          }
        }
      }
      setPreviewing(true);
      const payload: any = {
        target: { tenantCode: v.targetTenant, projectId: v.targetProject },
        entityTypes: v.entityTypes,
        mode: v.mode,
      };
      if (v.useBase) {
        payload.source = { useBase: true, scope: 'tenant' };
      } else {
        payload.source = {
          system: v.sourceSystem || 'local',
          tenantCode: v.sourceTenant,
          projectId: v.sourceProject,
        };
      }
      const r = await syncService.preview(payload);
      const data = r?.data || r;
      setPreview({
        previewId: data.previewId,
        expiresAt: data.expiresAt,
        summary: data.summary || {},
        diffs: data.diffs || [],
        totalDiffs: data.totalDiffs ?? (data.diffs || []).length,
      });
      // initialize decisions: default = overwrite for added/modified
      const init: Record<string, string> = {};
      (data.diffs || []).forEach((d: DiffEntry) => {
        if (d.operation !== 'unchanged') init[d.key] = 'overwrite';
      });
      setDecisions(init);
      setWizardStep(1);
      message.success(t('syncManager.msgs.previewSuccess', { total: data.totalDiffs ?? (data.diffs || []).length }));
    } catch (e: any) {
      if (e?.errorFields) return; // form validation
      message.error(t('syncManager.msgs.previewFailed', { error: e.message || e }));
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    if (!preview) return;
    Modal.confirm({
      title: t('syncManager.applyConfirm.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('syncManager.applyConfirm.content'),
      okText: t('syncManager.applyConfirm.ok'),
      cancelText: t('syncManager.applyConfirm.cancel'),
      onOk: async () => {
        setApplying(true);
        try {
          const decisionList = Object.entries(decisions).map(([key, action]) => ({ key, action }));
          const r = await syncService.apply({
            previewId: preview.previewId,
            decisions: decisionList,
            defaultAction,
          });
          const data = r?.data || r;
          message.success(t('syncManager.msgs.applySuccess', { applied: data?.applied || 0, skipped: data?.skipped || 0, failed: data?.failed || 0 }));
          const wizardValues = wizardForm.getFieldsValue();
          const targetTenant = wizardValues.targetTenant as string | undefined;
          const targetProject = wizardValues.targetProject as string | number | undefined;
          const entityTypes = (wizardValues.entityTypes as string[] | undefined) ?? [];
          if (targetTenant) {
            const currentTenant = authService.getCurrentTenant();
            if (currentTenant !== targetTenant) {
              authService.setCurrentTenant(targetTenant);
              window.dispatchEvent(
                new CustomEvent('tenantChanged', { detail: { tenantCode: targetTenant } }),
              );
            }
          }
          window.dispatchEvent(
            new CustomEvent('syncCompleted', {
              detail: {
                targetTenant,
                targetProject: targetProject != null ? String(targetProject) : undefined,
                entityTypes,
              },
            }),
          );
          setWizardStep(2);
          loadHistory();
        } catch (e: any) {
          message.error(t('syncManager.msgs.applyFailed', { error: e.message }));
        } finally {
          setApplying(false);
        }
      },
    });
  };

  const resetWizard = () => {
    setWizardStep(0);
    setPreview(null);
    setDecisions({});
    wizardForm.resetFields();
  };

  // 回到第 1 步但保留表单内容（用于 Preview 页"返回上一步重新调参数"）。
  // 注意：此时 wizardForm 仍持有旧值，AntD 在 step 0 重新渲染时会拿到这些值。
  const goBackToWizard = () => {
    setPreview(null);
    setDecisions({});
    setWizardStep(0);
  };

  // ── Base handlers ────────────────────────────────────────────────────────
  const handleSetBase = async () => {
    try {
      const v = await baseForm.validateFields();
      const r = await syncBaseService.setBase({
        scope: v.scope,
        projectId: String(v.projectId),
        entityTypes: v.entityTypes,
        description: v.description,
        force: v.force,
      });
      message.success(t('syncManager.base.saveSuccess'));
      setBaseModalOpen(false);
      baseForm.resetFields();
      loadBase();
    } catch (e: any) {
      if (e?.errorFields) return;
      if (e?.code === 'BASE_ALREADY_EXISTS' || /already exists/i.test(e?.message || '')) {
        Modal.confirm({
          title: t('syncManager.base.exists'),
          content: e.message,
          okText: t('syncManager.base.overwrite'),
          onOk: async () => {
            try {
              const v = await baseForm.validateFields();
              await syncBaseService.setBase({ ...v, projectId: String(v.projectId), force: true });
              message.success(t('syncManager.base.overwritten'));
              setBaseModalOpen(false);
              baseForm.resetFields();
              loadBase();
            } catch (err: any) {
              message.error(err.message);
            }
          },
        });
      } else {
        message.error(t('syncManager.msgs.setBaseFailed', { error: e.message }));
      }
    }
  };

  const handleDeleteBase = async (id: number, tenantCode?: string) => {
    try {
      await syncBaseService.remove(id, tenantCode);
      message.success(t('syncManager.base.deleted'));
      loadBase();
    } catch (e: any) {
      message.error(t('syncManager.msgs.deleteFailed', { error: e.message }));
    }
  };

  // ── History handlers ─────────────────────────────────────────────────────
  const handleRollback = async (jobId: string, targetTenant?: string) => {
    try {
      await syncService.rollback(jobId, targetTenant);
      message.success(t('syncManager.history.rollbackSuccess'));
      loadHistory();
    } catch (e: any) {
      message.error(t('syncManager.msgs.rollbackFailed', { error: e.message }));
    }
  };

  // ── Token handlers ───────────────────────────────────────────────────────
  const openRegisterTokenModal = (direction: 'outbound' | 'inbound') => {
    tokenForm.resetFields();
    tokenForm.setFieldsValue({ entityTypes: DEFAULT_ENTITY_TYPES });
    setTokenModalDirection(direction);
    setTokenModalOpen(true);
  };

  const handleRegisterToken = async () => {
    try {
      const v = await tokenForm.validateFields();
      await syncTokenService.register({
        alias: v.alias,
        baseUrl: tokenModalDirection === 'outbound' ? v.baseUrl : undefined,
        token: v.token,
        scope: { entityTypes: v.entityTypes },
        expiresAt: v.expiresAt,
        direction: tokenModalDirection,
      });
      message.success(t('syncManager.tokens.registerSuccess'));
      setTokenModalOpen(false);
      tokenForm.resetFields();
      loadTokens();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(t('syncManager.msgs.registerFailed', { error: e.message }));
    }
  };

  const handleRevokeToken = async (alias: string) => {
    try {
      await syncTokenService.revoke(alias);
      message.success(t('syncManager.tokens.revokeSuccess'));
      loadTokens();
    } catch (e: any) {
      message.error(t('syncManager.msgs.revokeFailed', { error: e.message }));
    }
  };

  const openEditTokenScopeModal = (record: TokenRecord) => {
    setEditingToken(record);
    tokenScopeForm.setFieldsValue({
      entityTypes: record.scope?.entityTypes?.length
        ? record.scope.entityTypes
        : DEFAULT_ENTITY_TYPES,
    });
    setTokenScopeModalOpen(true);
  };

  const handleUpdateTokenScope = async () => {
    if (!editingToken) return;
    try {
      const v = await tokenScopeForm.validateFields();
      await syncTokenService.updateScope(editingToken.alias, v.entityTypes);
      message.success(t('syncManager.tokens.updateScopeSuccess'));
      setTokenScopeModalOpen(false);
      setEditingToken(null);
      tokenScopeForm.resetFields();
      loadTokens();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(t('syncManager.msgs.updateScopeFailed', { error: e.message }));
    }
  };

  // ── Render: Wizard tab ───────────────────────────────────────────────────
  const handleSourceTenantBlurOrChange = useCallback(async (code: string) => {
    setSourceProjectsLoading(true);
    try {
      setSourceProjectOptions(await loadProjectsForTenant(code));
    } finally {
      setSourceProjectsLoading(false);
    }
    // tenant 切换时清空已选 project，避免错配
    wizardForm.setFieldValue('sourceProject', undefined);
  }, [loadProjectsForTenant, wizardForm]);

  const handleTargetTenantChange = useCallback(async (code: string) => {
    setTargetProjectsLoading(true);
    try {
      setTargetProjectOptions(await loadProjectsForTenant(code));
    } finally {
      setTargetProjectsLoading(false);
    }
    wizardForm.setFieldValue('targetProject', undefined);
  }, [loadProjectsForTenant, wizardForm]);

  const renderWizardForm = () => (
    <Card title={t('syncManager.wizard.step1Title')} size="small">
      <Form form={wizardForm} layout="vertical" initialValues={{ entityTypes: DEFAULT_ENTITY_TYPES, mode: 'incremental', useBase: false, sourceSystem: 'local' }}>
        <Alert
          type="info"
          showIcon
          message={t('syncManager.wizard.crossTenantNote')}
          style={{ marginBottom: 16 }}
        />
        <Row gutter={16}>
          <Col span={12}>
            <Title level={5}>{t('syncManager.wizard.source')}</Title>
            <Form.Item name="useBase" valuePropName="checked">
              <Checkbox>{t('syncManager.wizard.useBase')}</Checkbox>
            </Form.Item>
            <Form.Item shouldUpdate={(p, c) => p.useBase !== c.useBase} noStyle>
              {({ getFieldValue }) => !getFieldValue('useBase') && (
                <>
                  <Form.Item name="sourceSystem" label={t('syncManager.wizard.sourceSystem')} tooltip={t('syncManager.wizard.sourceSystemTooltip')}>
                    <Select placeholder="local" allowClear>
                      <Option value="local">{t('syncManager.wizard.sourceSystemLocal')}</Option>
                      {tokens
                        .filter(tk => !tk.revokedAt && (tk.direction ?? 'outbound') === 'outbound')
                        .map(tk => (
                          <Option key={tk.alias} value={tk.alias}>{tk.alias} ({tk.baseUrl})</Option>
                        ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="sourceTenant" label={t('syncManager.wizard.sourceTenant')} rules={[{ required: true }]}>
                    {tenantOptions.length > 0 ? (
                      <Select
                        showSearch
                        allowClear
                        placeholder={t('syncManager.wizard.selectTenant')}
                        optionFilterProp="label"
                        options={tenantOptions}
                        onChange={(v) => handleSourceTenantBlurOrChange(String(v || ''))}
                      />
                    ) : (
                      <Input
                        placeholder={t('syncManager.wizard.sourceTenantPlaceholder')}
                        onBlur={(e) => handleSourceTenantBlurOrChange(e.target.value)}
                      />
                    )}
                  </Form.Item>
                  <Form.Item name="sourceProject" label={t('syncManager.wizard.sourceProject')} rules={[{ required: true }]}>
                    <Select
                      showSearch
                      placeholder={t('syncManager.wizard.selectProject')}
                      optionFilterProp="label"
                      options={sourceProjectOptions}
                      loading={sourceProjectsLoading}
                      notFoundContent={
                        sourceProjectsLoading
                          ? t('common.loading')
                          : t('syncManager.wizard.noProjectAvailable')
                      }
                    />
                  </Form.Item>
                </>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Title level={5}>{t('syncManager.wizard.target')}</Title>
            <Form.Item name="targetTenant" label={t('syncManager.wizard.targetTenant')} rules={[{ required: true }]}>
              {tenantOptions.length > 0 ? (
                <Select
                  showSearch
                  allowClear
                  placeholder={t('syncManager.wizard.selectTenant')}
                  optionFilterProp="label"
                  options={tenantOptions}
                  onChange={(v) => handleTargetTenantChange(String(v || ''))}
                />
              ) : (
                <Input
                  placeholder={t('syncManager.wizard.targetTenantPlaceholder')}
                  onBlur={(e) => handleTargetTenantChange(e.target.value)}
                />
              )}
            </Form.Item>
            <Form.Item name="targetProject" label={t('syncManager.wizard.targetProject')} rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder={t('syncManager.wizard.selectProject')}
                optionFilterProp="label"
                options={targetProjectOptions}
                loading={targetProjectsLoading}
                notFoundContent={
                  targetProjectsLoading
                    ? t('common.loading')
                    : t('syncManager.wizard.noProjectAvailable')
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="entityTypes" label={t('syncManager.wizard.entityTypes')} rules={[{ required: true, message: t('syncManager.wizard.entityTypesRequired') }]}>
              <Checkbox.Group options={ENTITY_TYPES.map(v => ({ label: v, value: v }))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="mode" label={t('syncManager.wizard.syncMode')} rules={[{ required: true }]}>
              <Select>
                {MODES.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button type="primary" icon={<EyeOutlined />} loading={previewing} onClick={handlePreview}>
            {t('syncManager.wizard.btnPreview')}
          </Button>
          <Button onClick={resetWizard}>{t('syncManager.wizard.btnReset')}</Button>
        </Space>
      </Form>
    </Card>
  );

  const diffColumns = [
    {
      title: t('syncManager.diff.type'), dataIndex: 'entityType', width: 110,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { title: t('syncManager.diff.key'), dataIndex: 'key', ellipsis: true },
    {
      title: t('syncManager.diff.operation'), dataIndex: 'operation', width: 100,
      render: (op: string) => <Tag color={opTagColor[op] || 'default'}>{op}</Tag>,
    },
    {
      title: t('syncManager.diff.decision'), width: 180,
      render: (_: any, row: DiffEntry) => {
        if (row.operation === 'unchanged') return <Text type="secondary">—</Text>;
        const k = row.key;
        return (
          <Select
            size="small"
            style={{ width: 160 }}
            value={decisions[k] || 'overwrite'}
            onChange={(val) => setDecisions({ ...decisions, [k]: val })}
          >
            <Option value="overwrite">{t('syncManager.decision.overwrite')}</Option>
            <Option value="merge">{t('syncManager.decision.merge')}</Option>
            <Option value="skip">{t('syncManager.decision.skip')}</Option>
          </Select>
        );
      },
    },
    {
      title: t('syncManager.diff.details'), width: 80,
      render: (_: any, row: DiffEntry) => (
        <Button size="small" type="link" onClick={() => setDiffDetailRow(row)}>{t('syncManager.diff.view')}</Button>
      ),
    },
  ];

  const renderPreview = () => {
    if (!preview) return null;
    const summaryRows = Object.entries(preview.summary).map(([type, s]) => ({ type, ...s }));
    return (
      <Card
        title={`${t('syncManager.wizard.step2Title')} (Preview ${preview.previewId.slice(0, 8)}...)`}
        size="small"
        extra={<Text type="secondary">{t('syncManager.wizard.expiresAt')}: {new Date(preview.expiresAt).toLocaleString()}</Text>}
      >
        <Alert
          type="info"
          showIcon
          message={t('syncManager.wizard.previewSummary', { total: preview.totalDiffs })}
          style={{ marginBottom: 12 }}
        />
        <Table
          dataSource={summaryRows}
          rowKey="type"
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
          columns={[
            { title: t('syncManager.diff.type'), dataIndex: 'type' },
            { title: t('syncManager.diff.added'), dataIndex: 'added' },
            { title: t('syncManager.diff.modified'), dataIndex: 'modified' },
            { title: t('syncManager.diff.deleted'), dataIndex: 'deleted' },
            { title: t('syncManager.diff.unchanged'), dataIndex: 'unchanged' },
          ]}
        />
        <Space style={{ marginBottom: 12 }}>
          <Text>{t('syncManager.wizard.defaultActionLabel')}</Text>
          <Select size="small" value={defaultAction} onChange={setDefaultAction} style={{ width: 160 }}>
            <Option value="overwrite">{t('syncManager.decision.overwrite')}</Option>
            <Option value="merge">{t('syncManager.decision.merge')}</Option>
            <Option value="skip">{t('syncManager.decision.skip')}</Option>
          </Select>
        </Space>
        <Table
          dataSource={preview.diffs}
          rowKey={(r) => `${r.entityType}:${r.key}`}
          columns={diffColumns}
          size="small"
          pagination={{ pageSize: 20 }}
          scroll={{ y: 360 }}
        />
        <Space style={{ marginTop: 16 }}>
          <Button icon={<RollbackOutlined />} onClick={goBackToWizard}>
            {t('syncManager.wizard.btnBack')}
          </Button>
          <Button type="primary" icon={<CheckCircleOutlined />} loading={applying} onClick={handleApply}>
            {t('syncManager.wizard.btnApply')}
          </Button>
          <Button onClick={resetWizard}>{t('syncManager.wizard.btnCancel')}</Button>
        </Space>
      </Card>
    );
  };

  const renderResult = () => (
    <Card title={t('syncManager.wizard.step3Title')}>
      <Alert type="success" showIcon message={t('syncManager.wizard.doneAlert')} />
      <Space style={{ marginTop: 16 }}>
        <Button type="primary" onClick={resetWizard}>{t('syncManager.wizard.btnStartNew')}</Button>
      </Space>
    </Card>
  );

  const renderWizardTab = () => (
    <div>
      <Steps current={wizardStep} style={{ marginBottom: 24 }}>
        <Step title={t('syncManager.steps.configure')} icon={<SyncOutlined />} />
        <Step title={t('syncManager.steps.review')} icon={<EyeOutlined />} />
        <Step title={t('syncManager.steps.done')} icon={<CheckCircleOutlined />} />
      </Steps>
      {wizardStep === 0 && renderWizardForm()}
      {wizardStep === 1 && renderPreview()}
      {wizardStep === 2 && renderResult()}

      <Modal
        open={!!diffDetailRow}
        title={`${t('syncManager.diffDetail.title')}: ${diffDetailRow?.entityType} / ${diffDetailRow?.key}`}
        footer={null}
        width={720}
        onCancel={() => setDiffDetailRow(null)}
      >
        {diffDetailRow && (
          <div>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('syncManager.diffDetail.operation')}>
                <Tag color={opTagColor[diffDetailRow.operation]}>{diffDetailRow.operation}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('syncManager.diffDetail.sourceFp')}>{diffDetailRow.sourceFingerprint || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('syncManager.diffDetail.targetFp')}>{diffDetailRow.targetFingerprint || '—'}</Descriptions.Item>
            </Descriptions>
            {diffDetailRow.fields && (
              <>
                <Title level={5} style={{ marginTop: 16 }}>{t('syncManager.diffDetail.fieldChanges')}</Title>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={Object.entries(diffDetailRow.fields).map(([f, v]) => ({ field: f, ...v }))}
                  rowKey="field"
                  columns={[
                    { title: t('syncManager.diffDetail.field'), dataIndex: 'field', width: 160 },
                    { title: t('syncManager.diffDetail.before'), dataIndex: 'before', render: (v) => <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(v, null, 2)}</pre> },
                    { title: t('syncManager.diffDetail.after'), dataIndex: 'after', render: (v) => <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(v, null, 2)}</pre> },
                  ]}
                />
              </>
            )}
            {diffDetailRow.sourceData && diffDetailRow.operation === 'added' && (
              <>
                <Title level={5} style={{ marginTop: 16 }}>{t('syncManager.diffDetail.addedData')}</Title>
                <pre style={{ background: '#f5f5f5', padding: 12, fontSize: 12 }}>
                  {JSON.stringify(diffDetailRow.sourceData, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );

  // ── Render: Base tab ─────────────────────────────────────────────────────
  const baseTenantSelectOptions = [
    { value: '__current__', label: t('syncManager.viewTenant.current') },
    ...(isSuperAdmin ? [{ value: '__all__', label: t('syncManager.viewTenant.all') }] : []),
    ...tenantOptions,
  ];
  const baseViewTenantValue =
    baseViewTenant === null ? '__current__' :
    baseViewTenant === '' ? '__all__' :
    baseViewTenant;
  const onBaseViewTenantChange = (v: string) => {
    if (v === '__current__') setBaseViewTenant(null);
    else if (v === '__all__') setBaseViewTenant('');
    else setBaseViewTenant(v);
  };

  const renderBaseTab = () => (
    <Card
      title={t('syncManager.base.title')}
      extra={
        <Space wrap>
          <span>{t('syncManager.viewTenant.label')}:</span>
          <Select
            size="small"
            style={{ minWidth: 200 }}
            value={baseViewTenantValue}
            onChange={onBaseViewTenantChange}
            options={baseTenantSelectOptions}
            showSearch
            optionFilterProp="label"
          />
          <Button icon={<ReloadOutlined />} onClick={loadBase}>{t('syncManager.base.refresh')}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setBaseModalOpen(true)}>
            {t('syncManager.base.setBase')}
          </Button>
        </Space>
      }
    >
      <Paragraph type="secondary">
        {t('syncManager.base.description')}
      </Paragraph>

      <Title level={5}>{t('syncManager.base.tenantBase')}</Title>
      <Table
        loading={baseLoading}
        dataSource={baseList}
        rowKey={(r) => `${r.tenantCode || ''}-${r.id}`}
        size="small"
        pagination={false}
        locale={{ emptyText: <Empty description={t('syncManager.base.emptyTenant')} /> }}
        columns={[
          { title: t('syncManager.base.tenantCol'), dataIndex: 'tenantCode', width: 120, render: (v) => v || '—' },
          { title: t('syncManager.base.scope'), dataIndex: 'scope', width: 100, render: (v) => <Tag color={v === 'global' ? 'gold' : 'blue'}>{v}</Tag> },
          { title: 'Project ID', dataIndex: 'projectId', width: 120 },
          { title: t('syncManager.base.entityTypesCol'), dataIndex: 'entityTypes', render: (arr: string[]) => (arr || []).map(et => <Tag key={et}>{et}</Tag>) },
          { title: t('syncManager.base.descCol'), dataIndex: 'description', ellipsis: true },
          { title: t('syncManager.base.createdBy'), dataIndex: 'createdBy', width: 120 },
          { title: t('syncManager.base.createdAt'), dataIndex: 'createdAt', width: 170, render: (v) => v ? new Date(v).toLocaleString() : '—' },
          {
            title: t('syncManager.base.actions'), width: 100,
            render: (_: any, row: BaseRecord) => (
              <Popconfirm
                title={t('syncManager.base.confirmDelete')}
                onConfirm={() => handleDeleteBase(row.id, row.tenantCode)}
              >
                <Button danger size="small" icon={<DeleteOutlined />}>{t('syncManager.base.delete')}</Button>
              </Popconfirm>
            ),
          },
        ]}
      />

      {isSuperAdmin && (
        <>
          <Title level={5} style={{ marginTop: 24 }}>{t('syncManager.base.globalBase')}</Title>
          <Table
            dataSource={globalBaseList}
            rowKey="id"
            size="small"
            pagination={false}
            locale={{ emptyText: <Empty description={t('syncManager.base.emptyGlobal')} /> }}
            columns={[
              { title: t('syncManager.base.tenantCol'), dataIndex: 'tenantCode', width: 120 },
              { title: 'Project ID', dataIndex: 'projectId', width: 120 },
              { title: t('syncManager.base.entityTypesCol'), dataIndex: 'entityTypes', render: (arr: string[]) => (arr || []).map(et => <Tag key={et}>{et}</Tag>) },
              { title: t('syncManager.base.descCol'), dataIndex: 'description', ellipsis: true },
              { title: t('syncManager.base.createdAt'), dataIndex: 'createdAt', width: 170, render: (v) => v ? new Date(v).toLocaleString() : '—' },
            ]}
          />
        </>
      )}

      <Modal
        open={baseModalOpen}
        title={t('syncManager.base.modalTitle')}
        onCancel={() => setBaseModalOpen(false)}
        onOk={handleSetBase}
        okText={t('syncManager.base.save')}
      >
        <Form form={baseForm} layout="vertical" initialValues={{ scope: 'tenant', entityTypes: DEFAULT_ENTITY_TYPES }}>
          <Form.Item name="scope" label={t('syncManager.base.scope')} rules={[{ required: true }]}>
            <Select>
              <Option value="tenant">{t('syncManager.base.scopeTenant')}</Option>
              <Option value="global" disabled={!isSuperAdmin}>{t('syncManager.base.scopeSystem')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="projectId" label={t('syncManager.base.projectId')} rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder={t('syncManager.wizard.selectProject')}
              optionFilterProp="label"
              options={baseProjectOptions}
              notFoundContent={t('syncManager.wizard.noProjectAvailable')}
            />
          </Form.Item>
          <Form.Item name="entityTypes" label={t('syncManager.base.entityTypesCol')} rules={[{ required: true }]}>
            <Checkbox.Group options={ENTITY_TYPES.map(v => ({ label: v, value: v }))} />
          </Form.Item>
          <Form.Item name="description" label={t('syncManager.base.descCol')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="force" valuePropName="checked">
            <Checkbox>{t('syncManager.base.force')}</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  // ── Render: History tab ──────────────────────────────────────────────────
  const historyTenantSelectOptions = [
    { value: '__current__', label: t('syncManager.viewTenant.current') },
    ...(isSuperAdmin ? [{ value: '__all__', label: t('syncManager.viewTenant.all') }] : []),
    ...tenantOptions,
  ];
  const historyViewTenantValue =
    historyViewTenant === null ? '__current__' :
    historyViewTenant === '' ? '__all__' :
    historyViewTenant;
  const onHistoryViewTenantChange = (v: string) => {
    if (v === '__current__') setHistoryViewTenant(null);
    else if (v === '__all__') setHistoryViewTenant('');
    else setHistoryViewTenant(v);
  };

  const renderHistoryTab = () => (
    <Card
      title={t('syncManager.history.title')}
      extra={
        <Space wrap>
          <span>{t('syncManager.viewTenant.label')}:</span>
          <Select
            size="small"
            style={{ minWidth: 200 }}
            value={historyViewTenantValue}
            onChange={onHistoryViewTenantChange}
            options={historyTenantSelectOptions}
            showSearch
            optionFilterProp="label"
          />
          <Button icon={<ReloadOutlined />} onClick={loadHistory}>{t('syncManager.history.refresh')}</Button>
        </Space>
      }
    >
      <Table
        loading={historyLoading}
        dataSource={history}
        rowKey={(r) => `${r.target_tenant || ''}-${r.id}`}
        size="small"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
        columns={[
          { title: t('syncManager.history.jobId'), dataIndex: 'id', width: 240, ellipsis: true, render: (v) => <Tooltip title={v}><Text code>{v?.slice(0, 8)}…</Text></Tooltip> },
          { title: t('syncManager.history.source'), width: 180, render: (_, r: SyncJob) => `${r.source_tenant || '-'} / ${r.source_project || '-'}` },
          { title: t('syncManager.history.target'), width: 180, render: (_, r: SyncJob) => `${r.target_tenant || '-'} / ${r.target_project || '-'}` },
          { title: t('syncManager.history.mode'), dataIndex: 'mode', width: 140 },
          {
            title: t('syncManager.history.status'), dataIndex: 'status', width: 110,
            render: (v: string) => {
              // 状态值对齐后端 SyncEngine：completed / failed / rolled_back / running / queued
              const colors: Record<string, string> = {
                completed: 'green', failed: 'red', rolled_back: 'orange', running: 'blue', queued: 'default',
              };
              return <Tag color={colors[v] || 'default'}>{v}</Tag>;
            },
          },
          { title: t('syncManager.history.started'), dataIndex: 'started_at', width: 170, render: (v) => v ? new Date(v).toLocaleString() : '—' },
          { title: t('syncManager.history.finished'), dataIndex: 'finished_at', width: 170, render: (v) => v ? new Date(v).toLocaleString() : '—' },
          { title: t('syncManager.history.by'), dataIndex: 'triggered_by', width: 120 },
          {
            title: t('syncManager.history.actions'), width: 110, fixed: 'right' as const,
            render: (_, r: SyncJob) => (
              // 后端 apply 成功后状态写的是 'completed'，只有 completed 才允许 rollback
              r.status === 'completed' ? (
                <Popconfirm
                  title={t('syncManager.history.confirmRollback')}
                  onConfirm={() => handleRollback(r.id, r.target_tenant)}
                >
                  <Button size="small" icon={<RollbackOutlined />}>{t('syncManager.history.rollback')}</Button>
                </Popconfirm>
              ) : <Text type="secondary">—</Text>
            ),
          },
        ]}
        expandable={{
          expandedRowRender: (r: SyncJob) => (
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label={t('syncManager.history.entityTypes')}>{r.entity_types}</Descriptions.Item>
              <Descriptions.Item label={t('syncManager.history.appliedSummary')}>
                <pre style={{ margin: 0, fontSize: 12 }}>{r.applied_summary || '—'}</pre>
              </Descriptions.Item>
            </Descriptions>
          ),
        }}
      />
    </Card>
  );

  // ── Render: Tokens tab (super admin) ─────────────────────────────────────
  const renderTokensTab = () => {
    if (!isSuperAdmin) {
      return <Alert type="warning" showIcon message={t('syncManager.tokens.superAdminOnly')} />;
    }

    // 按 direction 过滤展示
    const outbound = tokens.filter(tk => (tk.direction ?? 'outbound') === 'outbound');
    const inbound = tokens.filter(tk => tk.direction === 'inbound');

    const sharedColumns = (showBaseUrl: boolean) => [
      { title: t('syncManager.tokens.alias'), dataIndex: 'alias', width: 160 },
      ...(showBaseUrl ? [{ title: t('syncManager.tokens.baseUrl'), dataIndex: 'baseUrl', ellipsis: true }] : []),
      {
        title: t('syncManager.tokens.allowedEntities'), dataIndex: 'scope', width: 240,
        render: (s: any) => (
          <Space size={4} wrap>
            {(s?.entityTypes || []).map((et: string) => <Tag key={et}>{et}</Tag>)}
          </Space>
        ),
      },
      { title: t('syncManager.tokens.lastUsed'), dataIndex: 'lastUsedAt', width: 170, render: (v: string) => v ? new Date(v).toLocaleString() : '—' },
      { title: t('syncManager.tokens.expiresAt'), dataIndex: 'expiresAt', width: 170, render: (v: string) => v ? new Date(v).toLocaleString() : '—' },
      {
        title: t('syncManager.tokens.status'), width: 100,
        render: (_: any, r: TokenRecord) =>
          r.revokedAt ? <Tag color="red">{t('syncManager.tokens.revoked')}</Tag> : <Tag color="green">{t('syncManager.tokens.active')}</Tag>,
      },
      {
        title: t('syncManager.tokens.actions'), width: 180,
        render: (_: any, r: TokenRecord) => (
          !r.revokedAt && (
            <Space size="small">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditTokenScopeModal(r)}
              >
                {t('syncManager.tokens.editScope')}
              </Button>
              <Popconfirm title={t('syncManager.tokens.confirmRevoke')} onConfirm={() => handleRevokeToken(r.alias)}>
                <Button danger size="small" icon={<DeleteOutlined />}>{t('syncManager.tokens.revoke')}</Button>
              </Popconfirm>
            </Space>
          )
        ),
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Outbound：本系统去调远端用 */}
        <Card
          title={t('syncManager.tokens.outboundTitle')}
          extra={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadTokens}>{t('syncManager.tokens.refresh')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openRegisterTokenModal('outbound')}>
                {t('syncManager.tokens.registerOutbound')}
              </Button>
            </Space>
          }
        >
          <Paragraph type="secondary">{t('syncManager.tokens.outboundDescription')}</Paragraph>
          <Table
            loading={tokenLoading}
            dataSource={outbound}
            rowKey="alias"
            size="small"
            pagination={false}
            locale={{ emptyText: <Empty description={t('syncManager.tokens.outboundEmpty')} /> }}
            columns={sharedColumns(true)}
          />
        </Card>

        {/* Inbound：远端调本系统时用来校验 */}
        <Card
          title={t('syncManager.tokens.inboundTitle')}
          extra={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadTokens}>{t('syncManager.tokens.refresh')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openRegisterTokenModal('inbound')}>
                {t('syncManager.tokens.registerInbound')}
              </Button>
            </Space>
          }
        >
          <Paragraph type="secondary">{t('syncManager.tokens.inboundDescription')}</Paragraph>
          <Table
            loading={tokenLoading}
            dataSource={inbound}
            rowKey="alias"
            size="small"
            pagination={false}
            locale={{ emptyText: <Empty description={t('syncManager.tokens.inboundEmpty')} /> }}
            columns={sharedColumns(false)}
          />
        </Card>

        {/* 共用 Register modal —— 按 tokenModalDirection 决定显示字段 */}
        <Modal
          open={tokenModalOpen}
          title={
            tokenModalDirection === 'outbound'
              ? t('syncManager.tokens.modalTitleOutbound')
              : t('syncManager.tokens.modalTitleInbound')
          }
          onCancel={() => setTokenModalOpen(false)}
          onOk={handleRegisterToken}
          okText={t('syncManager.tokens.save')}
          width={560}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              tokenModalDirection === 'outbound'
                ? t('syncManager.tokens.outboundHint')
                : t('syncManager.tokens.inboundHint')
            }
          />
          <Form form={tokenForm} layout="vertical" initialValues={{ entityTypes: DEFAULT_ENTITY_TYPES }}>
            <Form.Item name="alias" label={t('syncManager.tokens.alias')} rules={[{ required: true }]}>
              <Input placeholder={t('syncManager.tokens.aliasPlaceholder')} />
            </Form.Item>
            {tokenModalDirection === 'outbound' && (
              <Form.Item name="baseUrl" label={t('syncManager.tokens.baseUrl')} rules={[{ required: true, type: 'url' }]}>
                <Input placeholder={t('syncManager.tokens.baseUrlPlaceholder')} />
              </Form.Item>
            )}
            <Form.Item name="token" label={t('syncManager.tokens.tokenLabel')} rules={[{ required: true }]}>
              <Input.Password placeholder={t('syncManager.tokens.tokenPlaceholder')} />
            </Form.Item>
            <Form.Item name="entityTypes" label={t('syncManager.tokens.allowedEntityTypes')} rules={[{ required: true }]}>
              <Checkbox.Group options={ENTITY_TYPES.map(v => ({ label: v, value: v }))} />
            </Form.Item>
            <Form.Item name="expiresAt" label={t('syncManager.tokens.expiresAtLabel')}>
              <Input placeholder={t('syncManager.tokens.expiresAtPlaceholder')} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          open={tokenScopeModalOpen}
          title={t('syncManager.tokens.editScopeTitle', { alias: editingToken?.alias ?? '' })}
          onCancel={() => {
            setTokenScopeModalOpen(false);
            setEditingToken(null);
            tokenScopeForm.resetFields();
          }}
          onOk={handleUpdateTokenScope}
          okText={t('syncManager.tokens.save')}
          width={480}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('syncManager.tokens.editScopeHint')}
          />
          <Form form={tokenScopeForm} layout="vertical">
            <Form.Item
              name="entityTypes"
              label={t('syncManager.tokens.allowedEntityTypes')}
              rules={[{ required: true, message: t('syncManager.wizard.entityTypesRequired') }]}
            >
              <Checkbox.Group options={ENTITY_TYPES.map(v => ({ label: v, value: v }))} />
            </Form.Item>
          </Form>
        </Modal>
      </Space>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, background: '#fff', minHeight: '100%' }}>
      <Title level={3} style={{ marginTop: 0 }}>
        <Space>
          <SyncOutlined />
          {t('syncManager.title')}
        </Space>
      </Title>
      <Paragraph type="secondary">
        {t('syncManager.description')}
      </Paragraph>

      <Tabs defaultActiveKey="wizard" type="card">
        <TabPane
          tab={<span><SyncOutlined /> {t('syncManager.tabs.wizard')}</span>}
          key="wizard"
        >
          {renderWizardTab()}
        </TabPane>
        <TabPane
          tab={<span><StarFilled /> {t('syncManager.tabs.base')}</span>}
          key="base"
        >
          {renderBaseTab()}
        </TabPane>
        <TabPane
          tab={<span><HistoryOutlined /> {t('syncManager.tabs.history')}</span>}
          key="history"
        >
          {renderHistoryTab()}
        </TabPane>
        {isSuperAdmin && (
          <TabPane
            tab={<span><KeyOutlined /> {t('syncManager.tabs.tokens')}</span>}
            key="tokens"
          >
            {renderTokensTab()}
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default SyncManager;
